import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { Th, Td, Field } from "./Customers";
import { useAuth } from "@/contexts/AuthContext";

const TABS = [
  { id: "tb", label: "Trial Balance" },
  { id: "pl", label: "P&L Statement" },
  { id: "bs", label: "Balance Sheet" },
];

export default function Financials() {
  const { tenant } = useAuth();
  const [tab, setTab] = useState("tb");
  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]" data-testid="financials-page">
      <PageHeader title="Financial Statements" subtitle="Real-time Trial Balance, P&L and Balance Sheet — generated from journal entries." />
      <div className="mt-6 flex flex-wrap gap-1 border-b border-[#DEE2E6]">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`fs-tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === t.id ? "border-[#FF4F00] text-[#0A0A0A]" : "border-transparent text-[#495057] hover:text-[#0A0A0A]"}`}>{t.label}</button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "tb" && <TrialBalance currency={tenant?.currency} />}
        {tab === "pl" && <PL currency={tenant?.currency} />}
        {tab === "bs" && <BalanceSheet currency={tenant?.currency} />}
      </div>
    </div>
  );
}

function TrialBalance({ currency }) {
  const [period, setPeriod] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/finance/financials/trial-balance${period ? `?period=${period}` : ""}`).then(({ data }) => setData(data)); }, [period]);
  return (
    <>
      <div className="max-w-xs mb-4"><Field label="Period (YYYY-MM, optional)" value={period} onChange={setPeriod} testid="tb-period" /></div>
      {data && (
        <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="tb-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]"><tr><Th>Account</Th><Th>Type</Th><Th align="right">Debit</Th><Th align="right">Credit</Th><Th align="right">Balance</Th></tr></thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={r.account} className="border-b border-[#DEE2E6]" data-testid={`tb-row-${i}`}>
                  <Td className="font-mono text-xs">{r.account}</Td>
                  <Td><span className="text-[10px] uppercase tracking-[0.15em] text-[#495057]">{r.type}</span></Td>
                  <Td align="right" className="tabular-nums">{r.debit.toLocaleString()}</Td>
                  <Td align="right" className="tabular-nums">{r.credit.toLocaleString()}</Td>
                  <Td align="right" className={`tabular-nums font-bold ${r.balance < 0 ? "text-[#D90429]" : "text-[#0B7A75]"}`}>{r.balance.toLocaleString()}</Td>
                </tr>
              ))}
              {data.rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#495057]">No journal entries yet.</td></tr>}
            </tbody>
            <tfoot className="bg-[#F8F9FA] font-bold">
              <tr><Td colSpan={2}>Totals · {currency}</Td>
                <Td align="right" className="tabular-nums">{data.totals.debit.toLocaleString()}</Td>
                <Td align="right" className="tabular-nums">{data.totals.credit.toLocaleString()}</Td>
                <Td align="right" className={`tabular-nums ${Math.abs(data.totals.debit - data.totals.credit) < 0.01 ? "text-[#0B7A75]" : "text-[#D90429]"}`}>{Math.abs(data.totals.debit - data.totals.credit) < 0.01 ? "✓ Balanced" : "Imbalanced"}</Td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </>
  );
}

function PL({ currency }) {
  const [period, setPeriod] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/finance/financials/profit-and-loss${period ? `?period=${period}` : ""}`).then(({ data }) => setData(data)); }, [period]);
  return (
    <>
      <div className="max-w-xs mb-4"><Field label="Period (YYYY-MM, optional)" value={period} onChange={setPeriod} testid="pl-period" /></div>
      {data && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="pl-statement">
          <div className="border border-[#0A0A0A] bg-white p-5">
            <div className="text-xs uppercase tracking-[0.15em] text-[#0B7A75] mb-2">Revenue</div>
            {data.revenue.map((r) => (<div key={r.account} className="flex justify-between py-1 border-b border-[#DEE2E6] text-sm"><span className="font-mono text-xs">{r.account}</span><span className="tabular-nums">{r.amount.toLocaleString()}</span></div>))}
            <div className="flex justify-between mt-3 pt-2 border-t-2 border-[#0A0A0A] font-bold"><span>Total revenue</span><span className="tabular-nums text-[#0B7A75]">{currency} {data.total_revenue.toLocaleString()}</span></div>
          </div>
          <div className="border border-[#0A0A0A] bg-white p-5">
            <div className="text-xs uppercase tracking-[0.15em] text-[#D90429] mb-2">Expenses</div>
            {data.expenses.map((e) => (<div key={e.account} className="flex justify-between py-1 border-b border-[#DEE2E6] text-sm"><span className="font-mono text-xs">{e.account}</span><span className="tabular-nums">{e.amount.toLocaleString()}</span></div>))}
            <div className="flex justify-between mt-3 pt-2 border-t-2 border-[#0A0A0A] font-bold"><span>Total expense</span><span className="tabular-nums text-[#D90429]">{currency} {data.total_expense.toLocaleString()}</span></div>
          </div>
          <div className="md:col-span-2 border-2 border-[#0A0A0A] bg-[#F8F9FA] p-5 flex items-center justify-between">
            <div><div className="text-xs uppercase tracking-[0.15em] text-[#495057]">Net Income {data.period && `· ${data.period}`}</div><div className="text-[10px] text-[#495057] mt-1">{data.gross_margin_pct != null && `Margin ${data.gross_margin_pct}%`}</div></div>
            <div className={`text-3xl font-bold tabular-nums ${data.net_income < 0 ? "text-[#D90429]" : "text-[#0B7A75]"}`}>{currency} {data.net_income.toLocaleString()}</div>
          </div>
        </div>
      )}
    </>
  );
}

function BalanceSheet({ currency }) {
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/finance/financials/balance-sheet?as_of=${asOf}`).then(({ data }) => setData(data)); }, [asOf]);
  return (
    <>
      <div className="max-w-xs mb-4"><Field label="As of (YYYY-MM-DD)" value={asOf} onChange={setAsOf} testid="bs-as-of" /></div>
      {data && (
        <>
          <div className="mb-3"><span className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-0.5 ${data.balanced ? "border-[#0B7A75] text-[#0B7A75]" : "border-[#D90429] text-[#D90429]"}`} data-testid="bs-balanced">{data.balanced ? "✓ Balance Sheet balances" : "⚠ Out of balance"}</span></div>
          <div className="grid md:grid-cols-2 gap-4" data-testid="bs-statement">
            <div className="border border-[#0A0A0A] bg-white p-5">
              <div className="text-xs uppercase tracking-[0.15em] text-[#0B7A75] mb-2">Assets</div>
              {data.assets.map((a) => (<div key={a.account} className="flex justify-between py-1 border-b border-[#DEE2E6] text-sm"><span className="font-mono text-xs">{a.account}</span><span className="tabular-nums">{a.amount.toLocaleString()}</span></div>))}
              <div className="flex justify-between mt-3 pt-2 border-t-2 border-[#0A0A0A] font-bold"><span>Total assets</span><span className="tabular-nums">{currency} {data.total_assets.toLocaleString()}</span></div>
            </div>
            <div className="space-y-4">
              <div className="border border-[#0A0A0A] bg-white p-5">
                <div className="text-xs uppercase tracking-[0.15em] text-[#D90429] mb-2">Liabilities</div>
                {data.liabilities.map((l) => (<div key={l.account} className="flex justify-between py-1 border-b border-[#DEE2E6] text-sm"><span className="font-mono text-xs">{l.account}</span><span className="tabular-nums">{l.amount.toLocaleString()}</span></div>))}
                <div className="flex justify-between mt-3 pt-2 border-t border-[#0A0A0A] font-bold"><span>Total liabilities</span><span className="tabular-nums">{currency} {data.total_liabilities.toLocaleString()}</span></div>
              </div>
              <div className="border border-[#0A0A0A] bg-white p-5">
                <div className="text-xs uppercase tracking-[0.15em] text-[#FF4F00] mb-2">Equity</div>
                {data.equity.map((e) => (<div key={e.account} className="flex justify-between py-1 border-b border-[#DEE2E6] text-sm"><span className="font-mono text-xs">{e.account}</span><span className="tabular-nums">{e.amount.toLocaleString()}</span></div>))}
                <div className="flex justify-between mt-3 pt-2 border-t border-[#0A0A0A] font-bold"><span>Total equity</span><span className="tabular-nums">{currency} {data.total_equity.toLocaleString()}</span></div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
